import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Lock, TrendingUp, AlertTriangle, CheckCircle, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';

const schoolData = [
  { name: "Harvard", rate: 3.59, avgSat: 1550, avgGpa: 3.94 },
  { name: "Stanford", rate: 3.68, avgSat: 1540, avgGpa: 3.96 },
  { name: "MIT", rate: 3.96, avgSat: 1555, avgGpa: 3.96 },
  { name: "Columbia", rate: 3.93, avgSat: 1540, avgGpa: 3.93 },
  { name: "Yale", rate: 4.35, avgSat: 1540, avgGpa: 3.94 },
  { name: "Princeton", rate: 5.73, avgSat: 1540, avgGpa: 3.93 },
  { name: "UPenn", rate: 5.4, avgSat: 1535, avgGpa: 3.92 },
  { name: "Brown", rate: 5.0, avgSat: 1530, avgGpa: 3.94 },
  { name: "Duke", rate: 5.0, avgSat: 1540, avgGpa: 3.94 },
  { name: "Dartmouth", rate: 6.24, avgSat: 1525, avgGpa: 3.92 },
  { name: "Northwestern", rate: 6.84, avgSat: 1530, avgGpa: 3.92 },
  { name: "Vanderbilt", rate: 5.6, avgSat: 1530, avgGpa: 3.91 },
  { name: "Johns Hopkins", rate: 6.5, avgSat: 1535, avgGpa: 3.93 },
  { name: "Rice", rate: 7.7, avgSat: 1530, avgGpa: 3.95 },
  { name: "Cornell", rate: 7.87, avgSat: 1520, avgGpa: 3.90 },
  { name: "Georgetown", rate: 11.4, avgSat: 1490, avgGpa: 3.90 },
  { name: "Emory", rate: 11.4, avgSat: 1480, avgGpa: 3.88 },
  { name: "Carnegie Mellon", rate: 11.2, avgSat: 1510, avgGpa: 3.89 },
  { name: "USC", rate: 9.9, avgSat: 1490, avgGpa: 3.87 },
  { name: "UCLA", rate: 8.6, avgSat: 1465, avgGpa: 3.92 },
  { name: "NYU", rate: 8.0, avgSat: 1495, avgGpa: 3.83 },
  { name: "UMich", rate: 17.7, avgSat: 1460, avgGpa: 3.88 },
  { name: "UVA", rate: 16.3, avgSat: 1440, avgGpa: 3.86 },
  { name: "Boston College", rate: 16.0, avgSat: 1440, avgGpa: 3.85 },
];

function calcChance(gpa, sat, school) {
  let score = 0;
  const gpaDiff = gpa - school.avgGpa;
  const satDiff = sat - school.avgSat;
  score += gpaDiff * 30;
  score += satDiff * 0.05;
  let baseChance = school.rate;
  if (school.rate < 5) baseChance = school.rate * 1.3;
  else if (school.rate < 10) baseChance = school.rate * 1.5;
  else baseChance = school.rate * 2;
  let chance = baseChance + score;
  chance = Math.max(2, Math.min(65, chance));
  return Math.round(chance);
}

function getLabel(chance) {
  if (chance >= 40) return { text: "Strong Match", color: "text-green-600", icon: CheckCircle, bg: "bg-green-50" };
  if (chance >= 20) return { text: "Competitive", color: "text-yellow-600", icon: Minus, bg: "bg-yellow-50" };
  if (chance >= 10) return { text: "Reach", color: "text-orange-600", icon: AlertTriangle, bg: "bg-orange-50" };
  return { text: "High Reach", color: "text-red-600", icon: AlertTriangle, bg: "bg-red-50" };
}

export default function CalculatorPage() {
  const [gpa, setGpa] = useState('');
  const [sat, setSat] = useState('');
  const [results, setResults] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const handleCalculate = () => {
    const g = parseFloat(gpa);
    const s = parseInt(sat);
    if (!g || !s || g < 0 || g > 4.0 || s < 400 || s > 1600) return;
    const res = schoolData.map(school => ({
      ...school,
      chance: calcChance(g, s, school),
    })).sort((a, b) => b.chance - a.chance);
    setResults(res);
  };

  const displayed = results ? (showAll ? results : results.slice(0, 10)) : [];

  return (
    <div className="min-h-screen bg-white" data-testid="calculator-page">
      <SEO title="Admissions Calculator — See Where You'd Get In" description="Input your GPA and SAT score to see estimated admission chances at the top 25 universities. Free tool from Ivy League Consulting." />
      <MarketingNav />

      <section className="pt-16 bg-[#0F392B] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">Free Tool</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Where Would <span className="italic">You</span> Get In?
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto mb-10">
            Input your GPA and SAT score. See estimated chances at the top 25 universities. It takes 10 seconds.
          </p>
          <div className="max-w-md mx-auto bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/15">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-white/70 block mb-1.5 text-left">GPA (unweighted, 0.0 — 4.0)</label>
                <input type="number" step="0.01" min="0" max="4.0" placeholder="e.g. 3.7" value={gpa} onChange={e => setGpa(e.target.value)} className="w-full px-4 py-3 rounded-lg bg-white text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" data-testid="calc-gpa-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-white/70 block mb-1.5 text-left">SAT Score (400 — 1600)</label>
                <input type="number" step="10" min="400" max="1600" placeholder="e.g. 1450" value={sat} onChange={e => setSat(e.target.value)} className="w-full px-4 py-3 rounded-lg bg-white text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" data-testid="calc-sat-input" />
              </div>
              <Button onClick={handleCalculate} className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#0F392B] h-12 text-base font-medium" data-testid="calc-submit-btn">
                <BarChart3 className="mr-2 h-4 w-4" /> Calculate My Chances
              </Button>
            </div>
          </div>
        </div>
      </section>

      {results && (
        <section className="py-16 md:py-24" data-testid="calc-results-section">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Estimated Chances
            </h2>
            <p className="text-sm text-muted-foreground mb-8">Based on GPA: {gpa} | SAT: {sat}. Sorted by estimated match strength.</p>
            <div className="space-y-3">
              {displayed.map((s, i) => {
                const label = getLabel(s.chance);
                return (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-[#E2E2E2] hover:shadow-md transition-shadow" data-testid={`calc-result-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${label.bg} flex items-center justify-center`}>
                        <label.icon className={`h-4 w-4 ${label.color}`} strokeWidth={2} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.rate}% acceptance rate</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${label.color}`} style={{ fontFamily: "'Playfair Display', serif" }}>{s.chance}%</p>
                      <p className={`text-xs ${label.color}`}>{label.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {!showAll && results.length > 10 && (
              <button onClick={() => setShowAll(true)} className="mt-4 text-sm text-[#0F392B] font-medium hover:underline" data-testid="calc-show-all">Show all {results.length} schools</button>
            )}

            {/* Teaser / Lock */}
            <div className="mt-10 rounded-2xl bg-[#0F392B] p-8 text-center" data-testid="calc-teaser">
              <Lock className="h-8 w-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Want the full picture?
              </h3>
              <p className="text-sm text-white/70 mb-2 max-w-md mx-auto">
                This calculator uses GPA and SAT only. The portal's <span className="text-white font-medium">AI College Match Score</span> analyzes your complete profile — extracurriculars, essays, intended major, geographic factors, and institutional priorities.
              </p>
              <p className="text-sm text-white/70 mb-6 max-w-md mx-auto">
                Plus you'll get a <span className="text-white font-medium">45-minute strategy session</span> with a former Ivy League admissions officer who will review your positioning in depth.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/sign-up">
                  <Button className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-base" data-testid="calc-portal-cta">
                    Access the Full Portal — $99 <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center justify-center gap-4 mt-6 text-xs text-white/40">
                <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> AI Match Score</span>
                <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Essay AI Feedback</span>
                <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Document Vault</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {!results && (
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-medium tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>How It Works</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto mb-8">Enter your unweighted GPA and SAT score above. We'll estimate your admission chances at the top 25 universities based on historical data and current acceptance rates.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { step: "1", title: "Enter Your Stats", desc: "GPA and SAT score — that's all we need" },
                { step: "2", title: "See Your Chances", desc: "Estimated admission probability at 25 schools" },
                { step: "3", title: "Go Deeper in the Portal", desc: "AI Match Score analyzes your full profile" },
              ].map((s, i) => (
                <div key={i} className="p-5 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2] text-center">
                  <div className="w-8 h-8 rounded-full bg-[#0F392B] flex items-center justify-center mx-auto mb-3">
                    <span className="text-xs font-semibold text-white">{s.step}</span>
                  </div>
                  <h3 className="text-sm font-medium mb-1">{s.title}</h3>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <MarketingFooter />
    </div>
  );
}
